export function shortHash(hash?: string | null, head = 8): string {
  if (!hash) return '—';
  return hash.length > head + 4 ? `${hash.slice(0, head)}…${hash.slice(-2)}` : hash;
}

/**
 * Timestamps are stored and hashed in UTC — that is what makes a consent record
 * unambiguous years later, and the evidence export keeps it that way. On screen
 * we show the reader's own timezone, because a compliance officer in Bengaluru
 * should not have to add five and a half hours in their head to read their own
 * ledger. Both are always available: the column is labelled with the zone, and
 * the event detail shows UTC alongside.
 */
export function dateTime(value?: string | Date | null): string {
  if (!value) return '—';
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** The UTC form, for anywhere the exact stored value matters. */
export function dateTimeUtc(value?: string | Date | null): string {
  if (!value) return '—';
  const iso = new Date(value).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)} UTC`;
}

/**
 * Short name for the reader's timezone — 'IST' in India, whatever the browser
 * reports elsewhere. Used to label time columns honestly.
 */
export function localZoneLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat('en-IN', { timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? 'local';
  } catch {
    return 'local';
  }
}

export function date(value?: string | Date | null): string {
  if (!value) return '—';
  return new Date(value).toISOString().slice(0, 10);
}

export function relative(value?: string | Date | null): string {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} d ago`;
}

export function number(n?: number | null): string {
  return (n ?? 0).toLocaleString('en-IN');
}

export function retention(days?: number | null): string {
  if (!days) return 'until withdrawn';
  if (days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  return `${days} days`;
}

export function bytes(n?: number | null): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const LANG_NAMES: Record<string, string> = {
  en: 'English', hi: 'हिन्दी Hindi', mr: 'मराठी Marathi', ta: 'தமிழ் Tamil', te: 'తెలుగు Telugu',
  bn: 'বাংলা Bengali', gu: 'ગુજરાતી Gujarati', kn: 'ಕನ್ನಡ Kannada', ml: 'മലയാളം Malayalam',
  or: 'ଓଡ଼ିଆ Odia', pa: 'ਪੰਜਾਬੀ Punjabi', as: 'অসমীয়া Assamese', ur: 'اردو Urdu',
  sa: 'संस्कृतम् Sanskrit', ne: 'नेपाली Nepali', sd: 'سنڌي Sindhi', ks: 'کٲشُر Kashmiri',
  kok: 'कोंकणी Konkani', mai: 'मैथिली Maithili', doi: 'डोगरी Dogri', brx: 'बड़ो Bodo',
  mni: 'ꯃꯤꯇꯩ Manipuri', sat: 'ᱥᱟᱱᱛᱟᱲᱤ Santali',
};

/**
 * Writing direction per language (CM-13).
 *
 * Three Eighth Schedule languages use the Perso-Arabic script and run right to
 * left. A translator typing Urdu into a left-to-right textarea sees their
 * punctuation jump to the wrong end of the line, which reads as a bug in the
 * tool and produces genuinely wrong copy when they try to correct it by hand.
 */
export const RTL_LANGS = new Set(['ur', 'ks', 'sd']);

export function langDir(code: string): 'ltr' | 'rtl' {
  return RTL_LANGS.has(code) ? 'rtl' : 'ltr';
}

/**
 * A font stack that actually contains the script's glyphs. Without one the
 * browser draws boxes, and a reviewer cannot tell a missing font from a missing
 * translation.
 */
const SCRIPT_FONTS: Record<string, string> = {
  devanagari: '"Nirmala UI", "Kohinoor Devanagari", "Noto Sans Devanagari", sans-serif',
  bengali: '"Nirmala UI", "Kohinoor Bangla", "Noto Sans Bengali", sans-serif',
  gujarati: '"Nirmala UI", "Gujarati Sangam MN", "Noto Sans Gujarati", sans-serif',
  gurmukhi: '"Nirmala UI", "Gurmukhi MN", "Noto Sans Gurmukhi", sans-serif',
  kannada: '"Nirmala UI", "Kannada Sangam MN", "Noto Sans Kannada", sans-serif',
  malayalam: '"Nirmala UI", "Malayalam Sangam MN", "Noto Sans Malayalam", sans-serif',
  odia: '"Nirmala UI", "Oriya Sangam MN", "Noto Sans Oriya", sans-serif',
  tamil: '"Nirmala UI", "Tamil Sangam MN", "Noto Sans Tamil", sans-serif',
  telugu: '"Nirmala UI", "Telugu Sangam MN", "Noto Sans Telugu", sans-serif',
  arabic: '"Noto Nastaliq Urdu", "Urdu Typesetting", "Geeza Pro", "Noto Sans Arabic", serif',
  olchiki: '"Nirmala UI", "Noto Sans Ol Chiki", sans-serif',
  meetei: '"Nirmala UI", "Noto Sans Meetei Mayek", sans-serif',
};

const LANG_SCRIPT: Record<string, keyof typeof SCRIPT_FONTS> = {
  hi: 'devanagari', mr: 'devanagari', ne: 'devanagari', sa: 'devanagari',
  kok: 'devanagari', mai: 'devanagari', doi: 'devanagari', brx: 'devanagari',
  bn: 'bengali', as: 'bengali',
  gu: 'gujarati', pa: 'gurmukhi', kn: 'kannada', ml: 'malayalam',
  or: 'odia', ta: 'tamil', te: 'telugu',
  ur: 'arabic', ks: 'arabic', sd: 'arabic',
  sat: 'olchiki', mni: 'meetei',
};

export function langFont(code: string): string | undefined {
  const script = LANG_SCRIPT[code];
  return script ? SCRIPT_FONTS[script] : undefined;
}

export const ACTION_TONE: Record<string, 'ok' | 'warn' | 'bad' | ''> = {
  granted: 'ok',
  refreshed: 'ok',
  declined: 'warn',
  withdrawn: 'bad',
  expired: 'warn',
  informed: '',
  requested: '',
  re_requested: '',
  modified: '',
  notice_viewed: '',
  preference_centre_viewed: '',
  identity_verified: '',
  browser_linked: '',
  erasure_requested: 'warn',
  erasure_completed: 'bad',
  erasure_notice_sent: '',
};

/** "name, photograph, email +2": a list read as a sentence, not a row of chips. */
export function shortList(items: string[], max: number): string {
  if (!items.length) return '—';
  return items.length > max ? `${items.slice(0, max).join(', ')} +${items.length - max}` : items.join(', ');
}

/** "30 Sept 2026". Lists show the day; the exact time belongs on the record. */
export function day(value?: string | Date | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "30 Sept, 9:17 pm": a list that needs the time, without the seconds. */
export function dayTime(value?: string | Date | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}
